import { UiDefaults, UserProfile } from '../models/user-profile';
import { BUID, BUIDMid } from '../models/BUID';
import { getActiveOrganisation, getActiveOrganisationId } from './organisation-context.service';
import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { HTTPCONSTANTS } from "../http/http-constants";
import { BehaviorSubject } from 'rxjs';
import { environment } from 'apps/odin-ui/src/environments/environment';

export enum TestMidCondition {
  Test = 'test', // Dev/Staging Test MID
  Limited = 'limited', // Dev/Staging Non-test MID
  Real = 'real' // Production MID
}

@Injectable({ providedIn: 'root' })
export class MerchantService {

  private partnerKeySubject = new BehaviorSubject<string>(this.getPartnerIdFromProfile());
  private userIdSubject = new BehaviorSubject<string>(this.getuserIdFromProfile());
  private organisationNameSubject = new BehaviorSubject<string>(this.getOrganisationNameFromProfile());
  private organisationIdSubject = new BehaviorSubject<string>(getActiveOrganisationId(this.getUserprofile()));

  public partnerKey$ = this.partnerKeySubject.asObservable();
  public userId$ = this.userIdSubject.asObservable();
  public organisationName$ = this.organisationNameSubject.asObservable();
  /** Id of the organisation the user is currently acting in; re-emits after an organisation switch. */
  public organisationId$ = this.organisationIdSubject.asObservable();

  constructor(private httpClient: HttpClient) { }

  /**
   * Re-emits the profile-derived subjects from the stored profile. Call after
   * the profile changes (e.g. an organisation switch re-fetches it) so
   * subscribers aren't left with stale values seeded at construction.
   */
  public refreshFromProfile(): void {
    this.partnerKeySubject.next(this.getPartnerIdFromProfile());
    this.userIdSubject.next(this.getuserIdFromProfile());
    this.organisationNameSubject.next(this.getOrganisationNameFromProfile());
    this.organisationIdSubject.next(getActiveOrganisationId(this.getUserprofile()));
  }

  public getUserprofile(): UserProfile | null {
    const profileSetting = window.localStorage.getItem('profile');

    if (profileSetting === null || profileSetting === 'undefined') return null;

    const profileSettingParsed = JSON.parse(profileSetting) as UserProfile;

    return profileSettingParsed;
  }

  public setUserProfile(userProfile: UserProfile): void {
    if (userProfile === null) return;
    const profileSetting = JSON.stringify(userProfile);
    window.localStorage.setItem('profile', profileSetting);
  }

  public getCurrentBuid(): BUID | null {
    const merchantRef = localStorage.getItem('current-merchant-buid');
    if (merchantRef == null) return null;
    return JSON.parse(merchantRef) as BUID;
  }

  public getCurrentMid(): BUIDMid {
    const midRef = localStorage.getItem('current-merchant-mid');
    if (midRef == null || midRef === 'undefined') {
      return {} as BUIDMid;
    }
    return JSON.parse(midRef) as BUIDMid;
  }

  public getBuidByMonekId(monekId: string): BUID | null {
    const userProfile = this.getUserprofile();
    if (!userProfile || !userProfile.buids) return null;

    for (const buid of userProfile.buids) {
      if (buid.mids && Array.isArray(buid.mids)) {
        for (const mid of buid.mids) {
          if (mid.monekId === monekId) {
            return buid;
          }
        }
      }
    }
    return null;
  }

  public getCurrentBuidName(): string | null {
    const merchantRef = localStorage.getItem('current-merchant-buid');
    if (merchantRef == null) return null;
    return (JSON.parse(merchantRef) as BUID).name;
  }

  public getMerchantRef(): string {
    const buidJson = localStorage.getItem('current-merchant-buid');
    if (buidJson == null) return '';
    const buid = JSON.parse(buidJson) as BUID;
    return buid ? buid.id : '';
  }

  public SetUiDefaults(defaults: UiDefaults, callback: (updated: boolean, error?: unknown) => void): void {
    const userProfile = this.getUserprofile();
    if (userProfile === null) return;
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}`
      + `/identity-management/users/${userProfile.userId}/ui-defaults`;
    this.httpClient.put(url, defaults).subscribe(() => {
      // save change locally
      const profile = this.getUserprofile();
      if (profile === null) return;
      profile.uiDefaults = { ...profile.uiDefaults, ...defaults };
      this.setUserProfile(profile);

      callback(true);
    }, (error: unknown) => callback(false, error))
  }

  public AddFavouriteBuid(buid: BUID, callback: (updated: boolean, error?: unknown) => void): void {
    const userProfile = this.getUserprofile();
    if (userProfile === null) {
      callback(false, new Error('User profile not loaded'));
      return;
    }
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}`
      + `/identity-management/users/favourite/${buid.id}`;
    this.httpClient.put(url, {}).subscribe(() => {
      // save change locally
      const profile = this.getUserprofile();
      if (profile === null) return;
      profile.buids = profile.buids.map((b: BUID) => {
        if (b.id === buid.id) b.favourite = true;
        return b;
      });
      this.setUserProfile(profile);

      callback(true);
    }, (error: unknown) => callback(false, error))
  }
  public RemoveFavouriteBuid(buid: BUID, callback: (updated: boolean, error?: unknown) => void): void {
    const userProfile = this.getUserprofile();
    if (userProfile === null) {
      callback(false, new Error('User profile not loaded'));
      return;
    }
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}`
      + `/identity-management/users/favourite/${buid.id}`;
    this.httpClient.delete(url, {}).subscribe(() => {
      // save change locally
      const profile = this.getUserprofile();
      if (profile === null) return;
      profile.buids = profile.buids.map((b: BUID) => {
        if (b.id === buid.id) b.favourite = false;
        return b;
      });
      this.setUserProfile(profile);

      callback(true);
    }, (error: unknown) => callback(false, error))
  }

  public getPartnerIdFromProfile() {
    const profileSetting = this.getUserprofile();
    if (profileSetting === null) return '';

    return profileSetting.partnerId;
  }

  private getuserIdFromProfile() {
    const profileSetting = this.getUserprofile();
    if (profileSetting === null) return '';

    return profileSetting.userId;
  }

  private getOrganisationNameFromProfile() {
    const profileSetting = this.getUserprofile();
    if (profileSetting === null) return '';

    return getActiveOrganisation(profileSetting)?.organisationName
      ?? profileSetting.organisationName;
  }

  public getTestMidCondition(): TestMidCondition {
    if (environment.production) {
      return TestMidCondition.Real;
    }

    const currentMid = this.getCurrentMid();

    return currentMid.isTestMid
      ? TestMidCondition.Test
      : TestMidCondition.Limited;
  }
}
